import React, { Suspense, useEffect } from "react";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";
import { Button } from "@core/components/ui/button";
import { ClientOnly } from "@core/components/client-only";
import { Await, useNavigate, useNavigation, useParams } from "@remix-run/react";
import { useFormContext, FormProvider, useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/pro-light-svg-icons";
import { useDialog } from "@core/components/ui/use-dialog";
import { useApi } from "@core/api";
import Spinner from "@core/components/ui/Spinner";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { LabeledDataPoint } from "@core/components/LabeledDataPoint";
import { QuestionnaireCategory, QuestionnairePagedResults } from "@/apidtos";
import { EditorSection, useEditorFrameContext } from "@core/components/EditorFrame";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@core/components/ui/dialog";
import { cn } from "@core/lib/utils";
import { LabeledComboBox } from "@core/components/forms/LabeledComboBox";
import { LoadingOverlay } from "@core/components/ui/loading-overlay";

export const RenderCategoryQuestionnairesEditorSection: React.FC = () => {
  const { t } = useTranslation();
  const { setAccordionValue, accordionValue } = useEditorFrameContext();

  useEffect(() => {
    if (!accordionValue.includes("Questionnaires")) {
      setAccordionValue((prev) => [...prev, "Questionnaires"]);
    }
  }, [accordionValue, setAccordionValue]);

  return (
    <ClientOnly>
      <EditorSection title={t("ui.questionnaires", "Questionnaires")}>
        <CategoryQuestionnairesEditorSection />
      </EditorSection>
    </ClientOnly>
  );
};

const CategoryQuestionnairesEditorSection = () => {
  const api = useApi();
  const { t } = useTranslation();
  const { id } = useParams();
  const navigation = useNavigation();
  const { dialog, dismiss } = useDialog();

  const { data: category } = useQuery({
    queryKey: ["category", id],
    queryFn: () => api.GetCategoryById(Number(id), ["Children", "Parent", "Files.File"])
  });
  const { data: assignedQuestionnaires } = useQuery({
    queryKey: ["assignedQuestionnaires", id],
    queryFn: () => api.ListQuestionnaireCategory({ ProductCategoryId: id })
  });
  const { data: allQuestionnaires } = useQuery({
    queryKey: ["allQuestionnaires"],
    queryFn: () => api.ListQuestionnaire()
  });

  const deleteMutation = useMutation<boolean, any, QuestionnaireCategory>({
    mutationFn: async (questionnaireCategory) => {
      return await api.DeleteQuestionnaireCategoryById(questionnaireCategory.Id);
    },
    onSuccess: () => {
      location.reload();
      dismiss();
    },
  });

  const handleDelete = (questionnaireCategory: QuestionnaireCategory) => {
    dialog({
      title: t("ui.deleteQuestionnaireCategoryTitle", "Are you sure you want to delete this questionnaire category?"),
      description: <></>,
      action: (
        <div className="flex flex-1 justify-between">
          <Button type="button" onClick={() => dismiss()}>
            {t("ui.cancel", "Cancel")}
          </Button>
          <Button type="button" onClick={() => deleteMutation.mutate(questionnaireCategory)}>
            {t("ui.yes", "Yes")}
          </Button>
        </div>
      ),
    });
  };

  if (!category || !assignedQuestionnaires) return <Spinner />;

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <AssignQuestionnaireDialog allQuestionnaires={allQuestionnaires} categoryId={category.Id} />
      </div>
      {assignedQuestionnaires.Results.map((qc, index) => (
        <React.Fragment key={`questionnaire-${qc.Id}-category-${qc.ProductCategoryId}`}>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-4 items-start">
            <LabeledDataPoint
              label={t("ui.questionnaireId", "Questionnaire ID")}
              value={qc.QuestionnaireId}
            />
            <LabeledDataPoint
              label={t("ui.questionnaireKey", "Questionnaire Key")}
              value={qc.Questionnaire?.Key}
            />
            <Button
              type="button"
              className="self-end justify-self-end"
              variant="outline"
              disabled={navigation.state === "loading" || deleteMutation.isPending}
              onClick={() => handleDelete(qc)}
            >
              <span className="sr-only">{t("ui.delete", "Delete")}</span>
              {(navigation.state === "loading" || deleteMutation.isPending) && <Spinner className="me-2" />}
              <FontAwesomeIcon icon={faTrash} className="text-danger-500" />
            </Button>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

interface AssignQuestionnaireDialogProps {
  categoryId: number;
  allQuestionnaires?: QuestionnairePagedResults;
}

export const AssignQuestionnaireDialog = ({
  categoryId,
  allQuestionnaires,
}: AssignQuestionnaireDialogProps) => {
  const api = useApi();
  const { t } = useTranslation();

  const form = useForm<Omit<QuestionnaireCategory, "Category">>({
    mode: "onChange",
    defaultValues: {
      QuestionnaireId: undefined,
      ProductCategoryId: categoryId,
    },
  });

  const handleCreate = useMutation({
    mutationFn: (data: Omit<QuestionnaireCategory, "Category">) =>
      api.CreateQuestionnaireCategory(data),
    onSuccess: () => location.reload(),
  });

  const questionnaireId = form.watch("QuestionnaireId");

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className={cn("")}>
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          <span>{t("ui.assignQuestionnaire", "Assign Questionnaire")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("ui.assignQuestionnaire", "Assign Questionnaire")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("ui.assignQuestionnaire", "Assign Questionnaire")}
          </DialogDescription>
        </DialogHeader>
        <Suspense>
          <Await resolve={allQuestionnaires}>
            {(data) => {
              const questionnaires = data?.Results ?? [];
              return (
                <FormProvider {...form}>
                  <form
                    onSubmit={form.handleSubmit((data) => handleCreate.mutate(data))}
                    className="flex items-end space-x-2"
                  >
                    <LabeledComboBox
                      className="flex-grow"
                      label={t("ui.questionnaire", "Questionnaire")}
                      items={questionnaires}
                      itemSize={48}
                      isSelected={(item) => item.Id === questionnaireId}
                      onItemToggle={(item) =>
                        form.setValue("QuestionnaireId", item.Id, { shouldDirty: true })
                      }
                      renderItem={(item) =>
                        item.Key ? `${item.Key} - ID: ${item.Id}` : `ID: ${item.Id}`
                      }
                      getItemValue={(item) => item.Id.toString()}
                      placeholder={
                        questionnaireId == null
                          ? t("ui.selectQuestionnaire", "Select Questionnaire")
                          : `ID: ${questionnaires.find((a) => a.Id === questionnaireId)?.Id ?? ""}`
                      }
                    />
                    <Button
                      type="submit"
                      disabled={questionnaireId == null || handleCreate.isPending}
                      className="float-end"
                    >
                      {t("ui.assign", "Assign")}
                    </Button>
                    {handleCreate.isPending && <LoadingOverlay />}
                  </form>
                </FormProvider>
              );
            }}
          </Await>
        </Suspense>
        <DialogFooter>
          <DialogClose>{t("ui.close", "Close")}</DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

registerExtension(
  "admin.inventory.categories_.$id.route.questionnaires",
  RenderCategoryQuestionnairesEditorSection,
  "RenderCategoryQuestionnairesEditorSection"
);
